import { logger } from '@/logging/logger'
import { useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode'
import router from '@/router';
import { useLanguageStore } from '@/stores/language';
import { useRegistrationStore } from '@/stores/registration';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useLocationStore } from '@/stores/location';
import { usePreferenceStore } from '@/stores/preference';
import { useAuthStore } from '@/stores/auth';
import { useCollectStore } from '@/stores/collect';
import { useEncounterStore } from '@/stores/encounter';
import { useMrxStore } from '@/stores/mrx';
import { useTrackStore } from '@/stores/track';
import { load as loadMrx } from '@/utils/mrxEngine';

// resolves to the value App.vue hands its loading dialog, so it never rejects
export async function initializeApp(): Promise<boolean> {
    const languageStore = useLanguageStore();
    const registrationStore = useRegistrationStore();
    const partnerConfigStore = usePartnerConfigStore();
    const locationStore = useLocationStore();
    const preferenceStore = usePreferenceStore();
    const initialRouterQuery = { ...router.currentRoute.query };
    let code;
    try {
        if (useAuthStore().authenticated()) {
            logger.info('auth', 'session.restore.attempt', undefined, 'Session restore started with authenticated cookie state').then();

            // before the fetch: an offline engagement switch must still be caught
            useEncounterStore().syncEngagement()
            useCollectStore().syncEngagement()
            useMrxStore().syncEngagement()
            useTrackStore().syncEngagement()
            useGuidedModeStore().syncEngagement()

            // the first boot after the update hydrates all empty; guided mode persisted the same list
            if (useTrackStore().all.length === 0 && useGuidedModeStore().tracks.length > 0) {
                useTrackStore().setAll(useGuidedModeStore().tracks)
            }

            let response = await fetch('api/v3/conf.json')

            if (response.ok) {
                let conf = await response.json()
                logger.info('auth', 'session.restore.success', { status: response.status }, 'Session restore succeeded with remote config').then();

                preferenceStore.loadFromConfig(conf)

                useTrackStore().setAll(conf.tracks)
                useGuidedModeStore().setConfig({
                    clinicalContent: conf.clinicalContent,
                    tracks: conf.tracks,
                })
                partnerConfigStore.loadContext(conf)
                partnerConfigStore.loadFromConfig(conf.settings)
                locationStore.loadFromConfig(conf.settings)
                languageStore.all = conf.settings.languages
                code = conf.settings.code
                loadMrx()
            } else {
                logger.warn('auth', 'session.restore.failed', { status: response.status }, 'Session restore failed to load remote config').then();
            }
        } else {
            logger.warn('auth', 'session.restore.skipped', { authenticated: false }, 'Session restore skipped because authentication cookies are missing').then();
        }

        languageStore.load(code);
        languageStore.set(undefined, initialRouterQuery);

        await registrationStore.register();
    }
    catch (e: any) {
        logger.error('app.lifecycle', 'store.init', { message: e?.message }, 'Error during store init').then();
    }

    return true
}
